import { TextInput } from '@app/components/core/input/text/TextInput'
import { Initiative } from '@schema/initiative'
import { Alert } from '@app/components/core/alert/Alert'
import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../blocks/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { useEffect, useMemo, useState } from 'react'
import { useInitiativeAutoSave } from '../blocks/useInitiativeAutoSave'
import {
  ImageEditRow,
  ImagePreview,
} from '@app/components/blocks/imageEditRow/ImageEditRow'
import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { MultiLineTextInput } from '@app/components/core/input/text/MultiLineTextInput'
import { useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { Style } from 'inlines'
import { useToast } from '@app/util/hooks/useToast'

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}

export const MainDetailsStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: Partial<Initiative>
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const client = useClient()
  const [name, setName] = useState(initiative.title ?? '')
  const [description, setDescription] = useState(initiative.description ?? '')
  const [vision, setVision] = useState(initiative.vision ?? '')
  const [addedImages, setAddedImages] = useState<ImagePreview[]>([])
  const [removedImages, setRemovedImages] = useState<number[]>([])
  const [isNameValid, setIsNameValid] = useState(false)
  const { showFailToast } = useToast()

  const memoedObject = useMemo(
    () => ({
      title: name,
      description,
      vision,
    }),
    [name, description, vision],
  ) // Memoed since not all changes in this step need auto-save (images have their own flow)

  useInitiativeAutoSave(initiative.id, memoedObject)

  useEffect(() => {
    const nameValid = name && name.trim().length > 5
    // TODO: add uniqeness verification
    setIsNameValid(nameValid)
  }, [name, setIsNameValid])

  useEffect(() => {
    if (isNameValid) {
      completeStep(initiativeCreationSteps.mainDetails)
    } else {
      uncompleteStep(initiativeCreationSteps.mainDetails)
    }
  }, [isNameValid, completeStep, uncompleteStep])

  useEffect(() => {
    if (removedImages.length > 0) {
      Promise.all(
        removedImages.map((fileId) => client.call('delete-file', { fileId })),
      )
        .then(() => setRemovedImages([]))
        .catch(() => showFailToast())
    }

    if (addedImages.length > 0) {
      Promise.all(
        addedImages.map((preview) => {
          try {
            return client.stream('upload-file', {
              mimeType: preview.file.type,
              contents: preview.file,
              size: preview.file.size,
              fileName: `initiative-media-${Date.now()}-${preview.file.name}`,
              payload: { initiativeImage: { id: initiative.id } },
            })
          } finally {
            URL.revokeObjectURL(preview.preview)
          }
        }),
      )
        .then(() => setAddedImages([]))
        .catch(() => showFailToast())
    }
  }, [
    client,
    initiative.id,
    addedImages,
    removedImages,
    setRemovedImages,
    setAddedImages,
    showFailToast,
  ])

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={initiativeCreationSteps.mainDetails}
    >
      <Column gap="2.5rem" alignItems="stetch">
        <TextInput
          label={translations.nameLabel}
          required={true}
          labelStyle={labelStyle}
          placeholder={translations.namePlaceholder}
          text={name}
          setText={setName}
        />
        <TextInput
          label={translations.sloganLabel}
          labelStyle={labelStyle}
          placeholder={translations.sloganPlaceholder}
          text={description}
          setText={setDescription}
        />
        <Column alignItems="stetch">
          <Column gap={0} alignItems="stetch">
            <Text.Medium style={labelStyle}>
              {translations.imagesLabel}
            </Text.Medium>
            <Text.Medium style={{ color: 'var(--palette-blue-500)' }}>
              {translations.imagesSubtitle}
            </Text.Medium>
          </Column>
          <ImageEditRow
            existingImages={initiative.images}
            newImages={addedImages}
            removedImages={removedImages}
            setNewImages={setAddedImages}
            setRemovedImages={setRemovedImages}
            iconStyle={{
              color: 'var(--palette-blue-400)',
            }}
          />
          <Alert
            variant="info"
            size="medium"
            message={translations.imagesInfoAlert}
          />
        </Column>
        <Column gap="0.38rem" alignItems="stretch">
          <Text.Medium style={labelStyle}>
            {translations.contentLabel}
          </Text.Medium>
          <MultiLineTextInput
            placeholder={translations.contentPlaceholder}
            value={vision}
            setValue={setVision}
          />
        </Column>
      </Column>
    </CreationGuide>
  )
}
